import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import React from "react";

// Get prefix for library
const getLibraryPrefix = (library: string): string => {
  const prefixes: Record<string, string> = {
    fa: 'Fa',
    md: 'Md',
    hi: 'Hi',
    bs: 'Bs',
    ai: 'Ai',
    fi: 'Fi',
    io: 'Io',
    ri: 'Ri',
    bi: 'Bi',
    gr: 'Gr',
    go: 'Go',
    si: 'Si',
    im: 'Im',
    di: 'Di',
    cg: 'Cg',
    gi: 'Gi',
    pi: 'Pi',
    ci: 'Ci',
    lia: 'Lia',
    lu: 'Lu',
    rx: 'Rx',
    hi2: 'Hi',
    fa6: 'Fa',
    io5: 'Io',
  };
  return prefixes[library] || '';
};

// Cache for loaded libraries
const libraryCache: Record<string, any> = {};

// Cache for loaded icon components
const iconCache: Record<string, any> = {};

// Lazy load icon libraries
const loadIconLibrary = async (library: string) => {
  if (libraryCache[library]) {
    return libraryCache[library];
  }
  
  const libraries: Record<string, () => Promise<any>> = {
    fa: () => import("react-icons/fa"),
    md: () => import("react-icons/md"),
    hi: () => import("react-icons/hi"),
    bs: () => import("react-icons/bs"),
    ai: () => import("react-icons/ai"),
    fi: () => import("react-icons/fi"),
    io: () => import("react-icons/io"),
    ri: () => import("react-icons/ri"),
    bi: () => import("react-icons/bi"),
    gr: () => import("react-icons/gr"),
    go: () => import("react-icons/go"),
    si: () => import("react-icons/si"),
    im: () => import("react-icons/im"),
    di: () => import("react-icons/di"),
    cg: () => import("react-icons/cg"),
    gi: () => import("react-icons/gi"),
    pi: () => import("react-icons/pi"),
    ci: () => import("react-icons/ci"),
    lia: () => import("react-icons/lia"),
    lu: () => import("react-icons/lu"),
    rx: () => import("react-icons/rx"),
    hi2: () => import("react-icons/hi2"),
    fa6: () => import("react-icons/fa6"),
    io5: () => import("react-icons/io5"),
  };
  
  const loader = libraries[library];
  if (!loader) return null;
  
  try {
    const module = await loader();
    libraryCache[library] = module;
    return module;
  } catch (error) {
    console.error(`Failed to load library ${library}:`, error);
    return null;
  }
};

interface DynamicIconProps {
  iconName: string;
  className?: string;
  size?: number;
  fallback?: React.ReactNode;
}

export const DynamicIcon = ({ iconName, className = "", size, fallback = null }: DynamicIconProps) => {
  const [IconComponent, setIconComponent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!iconName) {
      setIsLoading(false);
      return;
    }

    // Check cache first
    if (iconCache[iconName]) {
      setIconComponent(iconCache[iconName]);
      setIsLoading(false);
      return;
    }

    // Parse icon name (format: "library:iconName" or just "iconName" for backward compatibility)
    let library = 'fa';
    let name = iconName;

    if (iconName.includes(':')) {
      [library, name] = iconName.split(':');
    }

    // Load icon
    const loadIcon = async () => {
      try {
        const iconModule = await loadIconLibrary(library);
        if (iconModule) {
          const prefix = getLibraryPrefix(library);
          const prefixedName = `${prefix}${name}`;
          const component = iconModule[prefixedName];
          
          // Check if it's a valid React component (function) or JSX element
          if (component) {
            iconCache[iconName] = component;
            setIconComponent(() => component);
          }
        }
      } catch (error) {
        console.error(`Failed to load icon ${iconName}:`, error);
      } finally {
        setIsLoading(false);
      }
    };

    loadIcon();
  }, [iconName]);

  if (isLoading) {
    return fallback || <Loader2 className={`${className} animate-spin`} size={size} />;
  }

  if (!IconComponent) {
    return fallback || <span className={className}>{iconName}</span>;
  }

  // Check if IconComponent is a function (React component) or object (JSX element)
  if (typeof IconComponent === 'function') {
    return React.createElement(IconComponent, { className, size });
  }
  
  // If it's already a JSX element, clone it with new props
  if (React.isValidElement(IconComponent)) {
    return React.cloneElement(IconComponent as React.ReactElement<any>, { className, size });
  }
  
  return fallback || <span className={className}>{iconName}</span>;
};
